import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { createCoursePath } from '@/data/services/coursePathServices';
import { StudioPermissions } from '@/enums/VersioningStatus';
import { TempLessonBlock } from '@/models/_deprecated/LessonBlock';
import alert from '@/utils/shared/alert';
import { adapterModuleCourse } from '@/utils/coursePath';
import { getErrorMessage } from '@/utils/getErrorMessage';
import {
  CourseStudioFormFields,
  useCoursePath,
  useLessonsBlockWithLessons,
} from './CourseStudio.hooks';
import { useCourseStudioBlocks } from './CourseStudioBlocks.hooks';
import { useCourseStudioDirectLessons } from './CourseStudioDirectLessons.hooks';
import CourseStudioForm from './CourseStudioForm';
import { useCourseStudio } from './info/CourseStudioInfo.hooks';
import { useCourseStudioObjectives } from './objectives/CourseStudioObjectives.hooks';

type Props = {
  defaultValues: CourseStudioFormFields;
  studioPermissions: StudioPermissions;
  resetForm?: (fields: Partial<CourseStudioFormFields>) => void;
};

export default function CourseStudioService(props: Props) {
  const { defaultValues, resetForm } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const { cacheManager } = useCoursePath();

  const { invalidateBlock, invalidate } = useLessonsBlockWithLessons(
    defaultValues?.coursePath?.slug,
  );

  async function onFinishUpdateBlocks(updatedLessonBlocks: TempLessonBlock[]) {
    await invalidate();
    await Promise.all(
      updatedLessonBlocks.map(async block => {
        await invalidateBlock(block.id);
      }),
    );
  }

  const { handleCreateObjectives, handleUpdateObjectives } =
    useCourseStudioObjectives();

  const { handleUpdateCourse, handleUpdateCoursePath } = useCourseStudio();

  const { handleUpdateBlocks, handleCreateBlocks } = useCourseStudioBlocks();

  const { handleUpdateDirectLessons, handleCreateDirectLessons } =
    useCourseStudioDirectLessons();

  async function onSubmitUpdate(
    currentData: CourseStudioFormFields,
    newData: CourseStudioFormFields,
  ) {
    let slug = currentData.coursePath?.slug;

    const {
      coursePath: currentCoursePath,
      course: currentCourse,
      objectives: currentObjectives,
      lessonBlocks: currentLessonBlocks,
      directLessons: currentDirectLessons,
    } = currentData;

    const {
      coursePath: newCoursePath,
      course: newCourse,
      objectives: newObjectives,
      lessonBlocks: newLessonBlocks,
      directLessons: newDirectLessons,
    } = newData;

    const fields = { ...currentData };

    if (currentCoursePath && newCoursePath) {
      const coursePathChanges = await handleUpdateCoursePath(
        currentCoursePath,
        newCoursePath,
      );

      fields.coursePath = { ...coursePathChanges };

      slug = coursePathChanges.slug;
    }

    if (currentCourse && newCourse) {
      const courseAdapter = adapterModuleCourse(newCourse);
      const courseChanges = await handleUpdateCourse(
        currentCourse,
        courseAdapter,
      );

      fields.course = { ...courseChanges, coursePathSlug: slug };
      fields.courseLineId = courseChanges.module?.courseLine?.id;
    }

    if (currentObjectives && newObjectives && slug) {
      const updatedObjectives = await handleUpdateObjectives(
        slug,
        currentObjectives,
        newObjectives,
      );

      fields.objectives = updatedObjectives;
    }

    if (currentDirectLessons && newDirectLessons && slug) {
      const updatedDirectLessons = await handleUpdateDirectLessons(
        slug,
        currentDirectLessons,
        newLessonBlocks,
        newDirectLessons,
      );

      fields.directLessons = updatedDirectLessons;
    }

    if (currentLessonBlocks && newLessonBlocks && slug) {
      const updatedLessonBlocks = await handleUpdateBlocks(
        slug,
        currentLessonBlocks,
        newLessonBlocks,
      );

      fields.lessonBlocks = updatedLessonBlocks;

      await onFinishUpdateBlocks(updatedLessonBlocks);
    }

    return fields;
  }

  async function onSubmitCreate(newData: CourseStudioFormFields) {
    const fields = { ...newData };

    const createdCoursePath = await createCoursePath({
      ...newData.coursePath,
      course: newData.course ? adapterModuleCourse(newData.course) : undefined,
    });

    fields.coursePath = createdCoursePath;

    if (newData.objectives?.length) {
      const createdObjectives = await handleCreateObjectives(
        createdCoursePath.slug,
        newData.objectives,
      );

      fields.objectives = createdObjectives;
    }

    if (newData.directLessons?.length) {
      const createdDirectLessons = await handleCreateDirectLessons(
        createdCoursePath.slug,
        newData.directLessons,
      );

      fields.directLessons = createdDirectLessons;
    }

    if (newData.lessonBlocks?.length) {
      const createdLessonBlocks = await handleCreateBlocks(
        createdCoursePath.slug,
        newData.lessonBlocks,
      );

      fields.lessonBlocks = createdLessonBlocks;
    }

    return fields;
  }

  async function onSubmit(newData: CourseStudioFormFields) {
    if (!defaultValues.coursePath && !defaultValues.course) {
      return await onSubmitCreate(newData);
    }

    return await onSubmitUpdate(defaultValues, newData);
  }

  const { mutateAsync: handleSubmit, isPending: loading } = useMutation(
    onSubmit,
    {
      onSuccess(data) {
        data.coursePath &&
          cacheManager.merge({
            ...data.coursePath,
            objectives: data.objectives,
          });
        alert.success(t('saved'));
        resetForm?.(data);
      },
      async onError(error) {
        alert.error(getErrorMessage(error));
      },
    },
  );

  return (
    <CourseStudioForm onSubmit={handleSubmit} loading={loading} {...props} />
  );
}
